import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import { Row, Col, Card, Badge, Spinner, Table } from 'react-bootstrap';
import { FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaCalendarAlt, FaUsers, FaChartLine, FaFilter, FaFileCsv, FaFilePdf } from "react-icons/fa";
import webservice from "./../../../services/webservice";
import ReactApexChart from 'react-apexcharts';
import Swal from 'sweetalert2';
import { ComponentLoader } from '../common/Loader';
import './Notification.css';


const NotificationTracking = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [goToPage, setGoToPage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState({
        total: null,
        success: null,
        failed: null,
        totalReach: null,
        avgSuccessRate: null
    });
    const [sortBy, setSortBy] = useState('sentAt:desc');
    const [filterStatus, setFilterStatus] = useState("All");
    const [filterTitle, setFilterTitle] = useState("");
    const [filterMessage, setFilterMessage] = useState("");
    const [filterStartDate, setFilterStartDate] = useState("");
    const [filterEndDate, setFilterEndDate] = useState("");
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const lastFetchParamsRef = useRef(null);
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCampaigns(currentPage);
        }, 400);

        return () => clearTimeout(timer);
    }, [currentPage, filterStatus, filterTitle, filterMessage, filterStartDate, filterEndDate, sortBy]);

    const renderSortHeader = (label, field, align = 'center') => {
        const [currentField, currentOrder] = sortBy.split(':');
        const isActive = currentField === field;

        return (
            <th
                className={align === 'start' ? 'ps-4' : 'text-center'}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => {
                    if (isActive) {
                        setSortBy(`${field}:${currentOrder === 'asc' ? 'desc' : 'asc'}`);
                    } else {
                        setSortBy(`${field}:desc`);
                    }
                }}
            >
                <div className={`d-flex align-items-center ${align === 'start' ? '' : 'justify-content-center'} gap-2`}>
                    {label}
                    <div className="d-flex flex-column align-items-center" style={{ opacity: isActive ? 1 : 0.3 }}>
                        <span style={{ fontSize: '9px', lineHeight: '1', color: isActive && currentOrder === 'asc' ? '#4f46e5' : '#94a3b8' }}>▲</span>
                        <span style={{ fontSize: '9px', lineHeight: '1', color: isActive && currentOrder === 'desc' ? '#4f46e5' : '#94a3b8' }}>▼</span>
                    </div>
                </div>
            </th>
        );
    };


    const fetchCampaigns = async (page = 1, statusOverride, titleOverride, messageOverride, startDateOverride, endDateOverride) => {
        try {
            setLoading(true);

            // Determine final values used for fetch (priority: argument override > state)
            const fStatus = statusOverride !== undefined ? statusOverride : filterStatus;
            const fTitle = titleOverride !== undefined ? titleOverride : filterTitle;
            const fMessage = messageOverride !== undefined ? messageOverride : filterMessage;
            const fStartDate = startDateOverride !== undefined ? startDateOverride : filterStartDate;
            const fEndDate = endDateOverride !== undefined ? endDateOverride : filterEndDate;

            const [currentField, currentOrder] = sortBy.split(':');

            const params = new URLSearchParams({
                page: page,
                limit: itemsPerPage,
                sortBy: currentField,
                sortOrder: currentOrder
            });

            if (fStatus && fStatus !== "All") params.append('status', fStatus);
            if (fTitle) params.append('title', fTitle);
            if (fMessage) params.append('message', fMessage);
            if (fStartDate) params.append('startDate', fStartDate);
            if (fEndDate) params.append('endDate', fEndDate);

            // Generate cache key using Resolved values to ensure unique identification
            const cacheKey = `CAMPAIGN_HISTORY_CACHE_${page}_${itemsPerPage}_${fStatus}_${fTitle}_${fMessage}_${fStartDate}_${fEndDate}_${sortBy}`;

            const cachedData = sessionStorage.getItem(cacheKey);
            if (cachedData) {
                const data = JSON.parse(cachedData);
                const cacheTimestamp = data.cacheTimestamp || 0;
                const fiveMinutes = 5 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > fiveMinutes;

                if (!isExpired) {
                    setCampaigns(data.campaigns || []);
                    setTotalCount(data.totalCount || 0);
                    setTotalPages(data.totalPages || 1);
                    setCurrentPage(data.currentPage || page);

                    // Restore stats from cache (previously missing)
                    if (data.stats) {
                        setStats({
                            total: data.stats.campaignsSent,
                            success: data.stats.totalSuccess,
                            failed: data.stats.totalFailed,
                            totalReach: data.stats.totalReach,
                            avgSuccessRate: data.stats.avgSuccessRate
                        });
                    }

                    lastFetchParamsRef.current = JSON.stringify({ currentPage: page, filterStatus: fStatus, filterTitle: fTitle, filterMessage: fMessage, filterStartDate: fStartDate, filterEndDate: fEndDate, sortBy });
                    return;
                } else {
                    sessionStorage.removeItem(cacheKey);
                }
            }



            const response = await axios.post(`${webservice}api/get-notifications?${params.toString()}`);
            if (response.data.success) {

                const cacheData = {
                    campaigns: response.data.data,
                    totalCount: response.data.pagination.totalCount,
                    totalPages: response.data.pagination.totalPages,
                    currentPage: response.data.pagination.currentPage,
                    stats: response.data.stats,
                    cacheTimestamp: Date.now()
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));

                setCampaigns(response.data.data || []);

                if (response.data.stats) {
                    setStats({
                        total: response.data.stats.campaignsSent,
                        success: response.data.stats.totalSuccess,
                        failed: response.data.stats.totalFailed,
                        totalReach: response.data.stats.totalReach,
                        avgSuccessRate: response.data.stats.avgSuccessRate
                    });
                }

                if (response.data.pagination) {
                    setTotalCount(response.data.pagination.totalCount);
                    setTotalPages(response.data.pagination.totalPages);
                    setCurrentPage(response.data.pagination.currentPage);
                }
            }
        } catch (error) {
            console.error("Error fetching campaigns:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoToPage = () => {
        if (!goToPage) {
            Swal.fire('Invalid Page', 'Please enter a page number.', 'warning');
            return;
        }
        const pageNum = parseInt(goToPage, 10);
        if (!pageNum || pageNum < 1 || pageNum > totalPages) {
            Swal.fire('Invalid Page', `Total pages: ${totalPages}. Please enter a valid page number.`, 'warning');
            return;
        }
        setCurrentPage(pageNum);
        setGoToPage('');
    };


    const getStatusBadge = (status) => {
        switch (status) {
            case 'Sent':
                return <span style={{ color: '#10b981', fontWeight: '600' }}>Sent</span>;
            case 'Partial':
                return <span style={{ color: '#f59e0b', fontWeight: '600' }}>Partial</span>;
            case 'Failed':
                return <span style={{ color: '#ef4444', fontWeight: '600' }}>Failed</span>;
            default:
                return <span style={{ color: '#6b7280', fontWeight: '600' }}>{status || 'Unknown'}</span>;
        }
    };

    // Chart Configuration
    const chartOptions = {
        chart: {
            height: 350,
            type: 'area',
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        colors: ['#6c5ce7', '#00b894'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [20, 100]
            }
        },
        xaxis: {
            categories: campaigns.slice(0, 7).reverse().map(c => new Date(c.sentAt).toLocaleDateString([], { month: 'short', day: 'numeric' })),
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: { show: true },
        grid: {
            borderColor: '#f1f1f1',
            strokeDashArray: 3
        },
        tooltip: {
            x: { format: 'dd/MM/yy HH:mm' },
        },
    };

    const chartSeries = [
        {
            name: 'Recipients',
            data: campaigns.slice(0, 7).reverse().map(c => c.recipientCount || 0)
        },
        {
            name: 'Success',
            data: campaigns.slice(0, 7).reverse().map(c => c.successCount || 0)
        }
    ];

    const getFailures = (camp) => {
        let failures = [];
        if (camp.failedUserDetails && camp.failedUserDetails.length > 0) {
            failures = camp.failedUserDetails;
        } else if (camp.failureDetails && camp.failureDetails.length > 0) {
            failures = camp.failureDetails;
        } else if (camp.failedInfo && camp.failedInfo.length > 0) {
            failures = camp.failedInfo;
        }
        return failures;
    };

    const exportToCSV = (camp) => {
        const failures = getFailures(camp);

        if (failures.length === 0) {
            Swal.fire({
                title: 'No Detailed Data',
                text: 'Detailed failure records were not found for this campaign. This usually happens for very old records or if individual delivery logs were not captured.',
                icon: 'info',
                confirmButtonColor: '#6c5ce7'
            });
            return;
        }

        Swal.fire({
            title: 'Generating CSV...',
            text: 'Please wait while we prepare your report.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
            timer: 1000,
            showConfirmButton: false
        });

        setTimeout(() => {
            const headers = ["User ID", "Title", "Message", "FCM Token", "Date & Time"];
            const rows = failures.map(f => [
                f._id || f.userId || 'N/A',
                camp.title || 'N/A',
                camp.message || 'N/A',
                f.fcmtoken || f.token || 'N/A',
                `${new Date(camp.sentAt).toLocaleDateString()} ${new Date(camp.sentAt).toLocaleTimeString()}`
            ]);

            let csvContent = "data:text/csv;charset=utf-8,"
                + headers.join(",") + "\n"
                + rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `Failure_Report_${camp.title.replace(/\s+/g, '_')}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }, 500);
    };

    const exportToPDF = (camp) => {
        const failures = getFailures(camp);

        if (failures.length === 0) {
            Swal.fire({
                title: 'No Detailed Data',
                text: 'Detailed failure records were not found for this campaign. This usually happens for very old records or if individual delivery logs were not captured.',
                icon: 'info',
                confirmButtonColor: '#6c5ce7'
            });
            return;
        }

        Swal.fire({
            title: 'Generating PDF...',
            text: 'Please wait while we prepare your print report.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
            timer: 1500,
            showConfirmButton: false
        });

        setTimeout(() => {
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                Swal.fire('Pop-up Blocked', 'Please allow pop-ups for this site to view the PDF report.', 'warning');
                return;
            }

            printWindow.document.write(`
                <html>
                    <head>
                        <title>Failure Report - ${camp.title}</title>
                        <style>
                            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #333; }
                            .header { text-align: center; border-bottom: 2px solid #6c5ce7; padding-bottom: 10px; margin-bottom: 20px; }
                            h2 { color: #6c5ce7; margin: 0; }
                            table { width: 100%; border-collapse: collapse; margin-top: 10px; table-layout: fixed; }
                            th, td { border: 1px solid #dfe6e9; padding: 10px; text-align: left; font-size: 11px; word-wrap: break-word; }
                            th { background-color: #f8f9fa; color: #636e72; font-weight: bold; text-transform: uppercase; }
                            tr:nth-child(even) { background-color: #fcfcfc; }
                            .footer { margin-top: 20px; font-size: 10px; color: #b2bec3; text-align: right; }
                        </style>
                    </head>
                    <body>
                        <div class="header">
                            <h2>Notification Failure Report</h2>
                            <p><strong>Campaign:</strong> ${camp.title || 'N/A'}<br/><strong>Sent At:</strong> ${new Date(camp.sentAt).toLocaleString()}</p>
                        </div>
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 20%">User ID</th>
                                    <th style="width: 20%">Title</th>
                                    <th style="width: 20%">Message</th>
                                    <th style="width: 25%">FCM Token</th>
                                    <th style="width: 15%">Date & Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${failures.map(f => `
                                    <tr>
                                        <td>${f._id || f.userId || 'N/A'}</td>
                                        <td>${camp.title || 'N/A'}</td>
                                        <td>${camp.message || 'N/A'}</td>
                                        <td>${f.fcmtoken || f.token || 'N/A'}</td>
                                        <td>${new Date(camp.sentAt).toLocaleDateString()}<br/>${new Date(camp.sentAt).toLocaleTimeString()}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                        <div class="footer">Generated by Medhorizone Admin Panel on ${new Date().toLocaleString()}</div>
                    </body>
                </html>
            `);
            printWindow.document.close();
            setTimeout(() => {
                printWindow.print();
            }, 500);
        }, 1000);
    };

    const filteredCampaigns = campaigns; // Backend already filtered/paged

    return (
        <div className="tracking-wrapper fade-in">
            <Row className="notify-mb-4 mt-4">
                <Col xl={3} md={6}>
                    <Card className="stat-card border-0 shadow-sm overflow-hidden" style={{ background: '#f5f3ff', color: '#5b21b6' , marginTop: "1.4rem" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="stat-icon-bg" style={{ width: '32px', height: '32px', minWidth: '32px', background: 'rgba(91, 33, 182, 0.1)', color: '#5b21b6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                                    <FaPaperPlane />
                                </div>
                                <span className="stat-trend" style={{ background: 'rgba(91, 33, 182, 0.1)', color: '#5b21b6', fontSize: '0.85rem', padding: '2px 8px', borderRadius: '12px' }}>
                                    {stats.total !== null ? `+ ${stats.total} total` : <ComponentLoader color="#5b21b6" size="14px" padding="0" />}
                                </span>
                            </div>
                            <h5 className="mb-0 fw-bold">{stats.total !== null ? stats.total : <ComponentLoader color="#5b21b6" size="20px" padding="5px" />}</h5>
                            <p className="mb-0" style={{ color: "#5b21b6", fontSize: '1rem', fontWeight: "600" }}>Campaigns Sent</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3} md={6}>
                    <Card className="stat-card border-0 shadow-sm overflow-hidden" style={{ background: '#ecfdf5', color: '#065f46' , marginTop: "1.4rem" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="stat-icon-bg" style={{ width: '32px', height: '32px', minWidth: '32px', background: 'rgba(6, 95, 70, 0.1)', color: '#065f46', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                                    <FaCheckCircle />
                                </div>
                                <Badge bg='#135335ff' className="border shadow-none" style={{ fontSize: '0.80rem', padding: '3px 6px', color: '#4bc491ff' }}>
                                    {stats.avgSuccessRate !== null ? `${stats.avgSuccessRate}% Rate` : <ComponentLoader color="#065f46" size="14px" padding="0" />}
                                </Badge>
                            </div>
                            <h5 className="mb-0 fw-bold">{stats.success !== null ? stats.success : <ComponentLoader color="#065f46" size="20px" padding="5px" />}</h5>
                            <p className="mb-0" style={{ color: "#065f46", fontSize: '1rem', fontWeight: "600" }}>Successful Deliveries</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3} md={6}>
                    <Card className="stat-card border-0 shadow-sm overflow-hidden" style={{ background: '#fef2f2', color: '#991b1b' , marginTop: "1.4rem" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="stat-icon-bg" style={{ width: '32px', height: '32px', minWidth: '32px', background: 'rgba(153, 27, 27, 0.1)', color: '#991b1b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                                    <FaExclamationCircle />
                                </div>
                            </div>
                            <h5 className="mb-0 fw-bold">{stats.failed !== null ? stats.failed : <ComponentLoader color="#991b1b" size="20px" padding="5px" />}</h5>
                            <p className="mb-0" style={{ color: "#991b1b", fontSize: '1rem', fontWeight: "600" }}>Delivery Failures</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3} md={6}>
                    <Card className="stat-card border-0 shadow-sm overflow-hidden" style={{ background: '#fffbeb', color: '#92400e' , marginTop: "1.4rem" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="stat-icon-bg" style={{ width: '32px', height: '32px', minWidth: '32px', background: 'rgba(146, 64, 14, 0.1)', color: '#92400e', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                                    <FaUsers />
                                </div>
                            </div>
                            <h5 className="mb-0 fw-bold">{stats.totalReach !== null ? stats.totalReach : <ComponentLoader color="#92400e" size="20px" padding="5px" />}</h5>
                            <p className="mb-0" style={{ color: "#92400e", fontSize: '1rem', fontWeight: "600" }}>Total Reach</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        
            {/* Advanced Filters Section */}
            <Card className="border-0 shadow-sm mb-4 filter-container">
                <Card.Body className="p-3">
                    <Row className="align-items-end g-3">
                        <Col lg={3} md={6}>
                            <label className="notify-filter-label">Title</label>
                            <input
                                type="text"
                                className="form-control filter-input"
                                placeholder="Search by title..."
                                value={filterTitle}
                                onChange={(e) => { setFilterTitle(e.target.value); setCurrentPage(1); }}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <label className="notify-filter-label">Body Message</label>
                            <input
                                type="text"
                                className="form-control filter-input"
                                placeholder="Search by message..."
                                value={filterMessage}
                                onChange={(e) => { setFilterMessage(e.target.value); setCurrentPage(1); }}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <label className="notify-filter-label">Start Date</label>
                            <input
                                type="date"
                                max={filterEndDate || new Date().toISOString().split('T')[0]}
                                className="form-control filter-input"
                                value={filterStartDate}
                                onChange={(e) => { setFilterStartDate(e.target.value); setCurrentPage(1); }}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <label className="notify-filter-label">End Date</label>
                            <input
                                type="date"
                                min={filterStartDate || new Date().toISOString().split('T')[0]}
                                className="form-control filter-input"
                                value={filterEndDate}
                                onChange={(e) => { setFilterEndDate(e.target.value); setCurrentPage(1); }}
                            />
                        </Col>
                        {(filterTitle || filterMessage || filterStartDate || filterEndDate) && (
                            <Col lg={2} md={12} className="d-flex gap-2">
                                <button
                                    style={{
                                        height: '54.6px',
                                        background: '#6d77d3ff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0 15px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        textTransform: 'uppercase',
                                        transition: 'all 0.2s',
                                        width: '100%'
                                    }}
                                    onClick={() => {
                                        setFilterTitle("");
                                        setFilterMessage("");
                                        setFilterStartDate("");
                                        setFilterEndDate("");
                                        setFilterStatus("All");
                                        setCurrentPage(1);
                                    }}
                                >
                                    CLEAR
                                </button>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden campaign-list-card">
                <Card.Header className="bg-white border-0 py-3">
                    <h5 className="mb-3 notify-text-dark fw-bold">Campaign History</h5>
                    <div className="tracking-tabs">
                        <button
                            className={`tracking-tab-btn ${filterStatus === 'All' ? 'active' : ''}`}
                            onClick={() => { setFilterStatus('All'); setCurrentPage(1); }}
                        >
                            All
                        </button>
                        <button
                            className={`tracking-tab-btn ${filterStatus === 'Success' ? 'active' : ''}`}
                            onClick={() => { setFilterStatus('Success'); setCurrentPage(1); }}
                        >
                            Success
                        </button>
                        <button
                            className={`tracking-tab-btn ${filterStatus === 'Partial' ? 'active' : ''}`}
                            onClick={() => { setFilterStatus('Partial'); setCurrentPage(1); }}
                        >
                            Partial
                        </button>
                        <button
                            className={`tracking-tab-btn ${filterStatus === 'Failure' ? 'active' : ''}`}
                            onClick={() => { setFilterStatus('Failure'); setCurrentPage(1); }}
                        >
                            Failure
                        </button>
                    </div>
                </Card.Header>
                <div className="pq-table-wrapper">
                    <div className="pq-table-scroll">
                        <table className="pq-table">
                            <thead>
                                <tr>
                                    {renderSortHeader('Campaign Details', 'title', 'start')}
                                    {renderSortHeader('Sent Date', 'sentAt')}
                                    {renderSortHeader('Recipients', 'recipientCount', 'start')}
                                    {renderSortHeader('Success', 'successCount')}
                                    {renderSortHeader('Failed', 'failureCount')}
                                    <th className="text-center">Status</th>
                                    {renderSortHeader('Admin', 'admin', 'start')}
                                    <th className="pe-4 text-center">Export</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" style={{ padding: '80px 0' }}>
                                            <ComponentLoader color="#6d77d3ff" size="40px" />
                                        </td>
                                    </tr>
                                ) : filteredCampaigns.length > 0 ? filteredCampaigns.map((camp, index) => (
                                    <tr key={index} className="campaign-row">
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="campaign-icon me-3">
                                                    <FaPaperPlane className="text-primary" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-bold">{camp.title || 'Untitled Campaign'}</h6>
                                                    <small className="text-muted text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                                                        {camp.message || 'No message content'}
                                                    </small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="fw-medium text-dark">{new Date(camp.sentAt).toLocaleDateString()}</span>
                                            <br />
                                            <small className="text-muted">{new Date(camp.sentAt).toLocaleTimeString()}</small>
                                        </td>
                                        <td><span className="badge bg-secondary bg-opacity-10 text-success px-3 py-2 fs-6" style={{ color: '#198754' }}>{camp.recipientCount || 0}</span></td>
                                        <td><span className="text-success fw-bold"><FaCheckCircle className="me-1" />{camp.successCount || 0}</span></td>
                                        <td><span className="text-danger fw-bold"><FaExclamationCircle className="me-1" />{camp.failureCount || 0}</span></td>
                                        <td className="text-center">{getStatusBadge(camp.status)}</td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <span className="fw-medium text-muted">
                                                    {camp.adminDetails && camp.adminDetails[0] ? camp.adminDetails[0].name : 'System Admin'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="btn btn-sm btn-primary text-white text-nowrap"
                                                style={{ minWidth: '100px' }}
                                                onClick={() => exportToCSV(camp)}
                                                disabled={!camp.failureCount || camp.failureCount === 0}
                                            >
                                                <FaFileCsv className="me-1" /> Export
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <div className="text-muted opacity-50 mb-3">
                                                <FaFilter size={40} />
                                            </div>
                                            <h6 className="text-muted">
                                                {(!filterTitle && !filterMessage && !filterStartDate && !filterEndDate && filterStatus === "All")
                                                    ? "Please apply a filter to search campaigns"
                                                    : `No records matching your search`}
                                            </h6>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {campaigns.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap mt-3 pagination-music">
                        <span className="pagination-info" style={{ color: '#6c757d', fontWeight: '500', fontSize: '14px' }}>
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} records
                        </span>
                        
                        <ul className="pagination mb-0" style={{ display: 'flex', gap: '5px', padding: 0, margin: 0, listStyle: 'none' }}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ borderRadius: '8px', padding: '8px 12px', border: '1px solid #e1e5eb', background: '#fff', color: '#495057', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-angle-double-left" />
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{ borderRadius: '8px', padding: '8px 12px', border: '1px solid #e1e5eb', background: '#fff', color: '#495057', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-angle-left" />
                                </button>
                            </li>

                            {[...Array(totalPages)].map((_, i) => i + 1).filter(page => Math.abs(currentPage - page) <= 2).map(page => (
                                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(page)}
                                        style={currentPage === page ? { borderRadius: '8px', padding: '8px 15px', border: 'none', background: '#8b5cf6', color: '#fff', fontWeight: '600' } : { borderRadius: '8px', padding: '8px 15px', border: '1px solid #e1e5eb', background: '#fff', color: '#495057' }}
                                    >
                                        {page}
                                    </button>
                                </li>
                            ))}

                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(next => Math.min(next + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0} style={{ borderRadius: '8px', padding: '8px 12px', border: '1px solid #e1e5eb', background: '#fff', color: '#495057', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-angle-right" />
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} style={{ borderRadius: '8px', padding: '8px 12px', border: '1px solid #e1e5eb', background: '#fff', color: '#495057', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-angle-double-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </Card>

            <style dangerouslySetInnerHTML={{
                __html: `
                .stat-card {
                    transition: transform 0.3s ease;
                }
                .stat-card:hover {
                    transform: translateY(-5px);
                }
                .stat-icon-bg {
                    width: 40px;
                    height: 40px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                }
                .stat-trend {
                    font-size: 0.75rem;
                    background: rgba(255, 255, 255, 0.2);
                    padding: 2px 8px;
                    border-radius: 20px;
                }
                .badge-rounded {
                    border-radius: 6px;
                    padding: 6px 10px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .tracking-tabs {
                    display: flex;
                    gap: 15px;
                    border-bottom: 2px solid #f1f3f5;
                    padding-bottom: 2px;
            
                }
                .tracking-tab-btn {
                    border: none;
                    background: none;
                    padding: 8px 12px;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #6accafff;
                    position: relative;
                    transition: all 0.2s;
                }
                .tracking-tab-btn:hover {
                    color: #6c5ce7;
                }
                .tracking-tab-btn.active {
                    color: #6c5ce7;
                }
                .tracking-tab-btn.active::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 100%;
                    height: 2px;
                    background: #6c5ce7;
                }
                .custom-tracking-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    width: 100%;
                }
                .custom-tracking-table thead th {
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    font-weight: 700;
                    color: #636e72;
                    background-color: #f8f9fa;
                    border: none !important;
                    padding: 15px 10px;
                }
                .custom-tracking-table tbody td {
                    border: none !important;
                    border-bottom: 1px solid #f1f3f5 !important;
                    padding: 15px 10px;
                }
                .custom-tracking-table tbody tr:last-child td {
                    border-bottom: none !important;
                }
                .tracking-text-primary {
                    color: #2d3436;
                    font-weight: 600;
                }
                .tracking-text-secondary {
                    color: #636e72;
                }
                .custom-tracking-table tbody tr:hover {
                    background-color: #f8f9fa !important;
                }
                .admin-badge {
                    background-color: #f1f3f5 !important;
                    color: #6c5ce7 !important;
                    font-weight: 500;
                    border-radius: 4px;
                }
                .status-sent {
                    background-color: #2ecc71 !important;
                    color: #fff !important;
                }
                .status-partial {
                    background-color: #3498db !important;
                    color: #fff !important;
                }
                .status-failed {
                    background-color: #e74c3c !important;
                    color: #fff !important;
                }
                .uppercase { text-transform: uppercase; }
            `}} />
        </div>
    );
};

export default NotificationTracking;